import { ConfirmDialog } from "@/components/common/ConfirmDialog/ConfirmDialog"
import { Checkbox } from "@/components/ui/checkbox"
import { Skeleton } from "@/components/ui/skeleton"
import { usePermissionGuard } from "@/hooks/usePermissionGuard"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { IconTrash } from "@tabler/icons-react"
import { Pencil, StickyNote, Trash2, TriangleAlert } from "lucide-react"
import { useState } from "react"
import {
  NOTE_WRITE_PERMISSION,
  useAddNoteAttachments,
  useCreateNote,
  useNotes,
  useRemoveNote,
  useRemoveNoteAttachment,
  useUpdateNote,
  type NoteEntityType,
  type NoteOut,
  type UpdateNoteInput,
} from "../../../api/notesApi"
import { formatDate } from "../../../utils/date"
import { McPill } from "../McPill"
import { SectionLabel } from "../SectionLabel"
import { TipIconButton } from "../TipIconButton"
import { NoteAttachmentList } from "./NoteAttachmentList"
import { NoteComposer, type NoteComposerValue } from "./NoteComposer"
import { NoteText } from "./NoteText"

interface NotesSectionProps {
  entityType: NoteEntityType
  entityId: string
  /** Inside a section that already has its own heading and frame (the supplier panel's
   *  collapsible cards): drops this one's border and heading. */
  embedded?: boolean
}

/**
 * Internal notes on one inquiry or one supplier: the list, a composer and, for
 * people allowed to write, edit and remove on each note. Readers get the list only.
 * Only supplier notes can warn (the extra checkbox and toggle below).
 */
export function NotesSection({
  entityType,
  entityId,
  embedded = false,
}: NotesSectionProps) {
  const { translate } = useTranslation()
  const canWrite = usePermissionGuard(NOTE_WRITE_PERMISSION[entityType]).canRun
  const isSupplier = entityType === "supplier"

  const {
    data: notes = [],
    isLoading,
    isError,
  } = useNotes(entityType, entityId)
  const createNote = useCreateNote(entityType)
  const updateNote = useUpdateNote(entityType)
  const removeNote = useRemoveNote(entityType)
  const addAttachments = useAddNoteAttachments(entityType)
  const removeAttachment = useRemoveNoteAttachment(entityType)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [removing, setRemoving] = useState<NoteOut | null>(null)
  // Attachments taken out while editing. They stay stored until Save, so Cancel
  // brings them back: a click on the remove button is not yet the removal.
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set())

  function startEditing(id: string | null) {
    setEditingId(id)
    setRemovedIds(new Set())
  }

  const keptAttachments = (note: NoteOut) =>
    note.attachments.filter((attachment) => !removedIds.has(attachment.id))

  async function saveEdit(note: NoteOut, value: NoteComposerValue) {
    const changes: Omit<UpdateNoteInput, "id"> = {}
    if (value.body !== note.body) changes.body = value.body
    if (isSupplier && value.warnOnInquiry !== note.warnOnInquiry)
      changes.warnOnInquiry = value.warnOnInquiry
    if (Object.keys(changes).length > 0)
      await updateNote.mutateAsync({ id: note.id, ...changes })
    // Before the new files: the server counts both toward the limit. One that went
    // through leaves the staged set, so a retry after a failure does not repeat it.
    for (const attachmentId of removedIds) {
      await removeAttachment.mutateAsync({ id: note.id, attachmentId })
      setRemovedIds((prev) => {
        const next = new Set(prev)
        next.delete(attachmentId)
        return next
      })
    }
    if (value.files.length > 0)
      await addAttachments.mutateAsync({ id: note.id, files: value.files })
    startEditing(null)
  }

  return (
    <div className={embedded ? "px-4 py-4" : "border-b border-grey4 px-4 py-4"}>
      {!embedded && (
        <SectionLabel className="mb-3 flex items-center gap-1.5">
          <StickyNote className="h-3.5 w-3.5" />
          {translate("MC_NOTES_TITLE")}
        </SectionLabel>
      )}

      {canWrite && (
        <div className="mb-4">
          <NoteComposer
            submitLabel={translate("MC_NOTE_ADD")}
            showWarnToggle={isSupplier}
            onSubmit={(value) =>
              createNote.mutateAsync({
                entityType,
                entityId,
                body: value.body,
                files: value.files,
                // An inquiry note cannot warn: the server refuses the flag there.
                ...(isSupplier ? { warnOnInquiry: value.warnOnInquiry } : {}),
              })
            }
          />
        </div>
      )}

      {isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : isError ? (
        <p className="text-[12.5px] text-grey5">
          {translate("MC_NOTES_LOAD_ERROR")}
        </p>
      ) : notes.length === 0 ? (
        <p className="text-[12.5px] text-grey5">
          {translate("MC_NOTES_EMPTY")}
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {notes.map((note) => (
            <li
              key={note.id}
              className="rounded-md border border-grey4 bg-grey3 p-3"
            >
              <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px] text-grey5">
                <span className="font-medium text-white">{note.createdBy}</span>
                <span>{formatDate(note.createdAt)}</span>
                {note.edited && <span>{translate("MC_NOTE_EDITED")}</span>}
                {note.warnOnInquiry && (
                  <McPill tone="attention" icon={TriangleAlert}>
                    {translate("MC_NOTE_WARN_ACTIVE")}
                  </McPill>
                )}
                {canWrite && editingId !== note.id && (
                  <div className="ml-auto flex gap-1">
                    <TipIconButton
                      label={translate("MC_NOTE_EDIT")}
                      onClick={() => startEditing(note.id)}
                    >
                      <Pencil size={13} />
                    </TipIconButton>
                    <TipIconButton
                      label={translate("MC_NOTE_REMOVE")}
                      onClick={() => setRemoving(note)}
                    >
                      <Trash2 size={13} />
                    </TipIconButton>
                  </div>
                )}
              </div>

              {editingId === note.id ? (
                <div className="flex flex-col gap-3">
                  <NoteAttachmentList
                    attachments={keptAttachments(note)}
                    canRemove
                    onRemove={(attachmentId) =>
                      setRemovedIds((prev) => new Set(prev).add(attachmentId))
                    }
                  />
                  <NoteComposer
                    submitLabel={translate("MC_NOTE_SAVE")}
                    initialBody={note.body}
                    initialWarn={note.warnOnInquiry}
                    existingAttachmentCount={keptAttachments(note).length}
                    hasPendingChanges={removedIds.size > 0}
                    showWarnToggle={isSupplier}
                    onSubmit={(value) => saveEdit(note, value)}
                    onCancel={() => startEditing(null)}
                    autoFocus
                  />
                </div>
              ) : (
                <div className="flex flex-col gap-2.5">
                  <p className="text-[13px] text-white">
                    <NoteText body={note.body} />
                  </p>
                  <NoteAttachmentList attachments={note.attachments} />
                  {isSupplier && canWrite && (
                    <label className="flex items-center gap-2 text-[12px] text-grey5">
                      <Checkbox
                        checked={note.warnOnInquiry}
                        disabled={updateNote.isPending}
                        onCheckedChange={(checked) =>
                          updateNote.mutate({
                            id: note.id,
                            warnOnInquiry: checked === true,
                          })
                        }
                      />
                      {translate("MC_NOTE_WARN_ON_INQUIRY")}
                    </label>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {removing && (
        <ConfirmDialog
          title={translate("MC_NOTE_REMOVE_CONFIRM_TITLE")}
          message={translate("MC_NOTE_REMOVE_CONFIRM_DESC")}
          confirmLabel={translate("MC_NOTE_REMOVE")}
          cancelLabel={translate("MC_DECL_BTN_CANCEL")}
          variant="danger"
          icon={IconTrash}
          isLoading={removeNote.isPending}
          onConfirm={() =>
            removeNote.mutate(removing.id, {
              onSuccess: () => setRemoving(null),
            })
          }
          onCancel={() => setRemoving(null)}
        />
      )}
    </div>
  )
}
